import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { Box, CompassColor, Link, Stack } from "@noom/wax-component-library";

import { NoomEmailAddressLink } from "@/components/noom-branding";
import {
  NoomMedMoreOptionsSelection,
  PrimaryCareProviderSelection,
} from "@/constants";
import { useAppContext } from "@/contexts";
import { useProgramResult, ValuePropsVariation } from "@/hooks";
import { partnerOffersPrograms } from "@/utils/programs";

import { DisclaimerText } from "./shared";

const lastingResultsDisclaimerKeySuffixMap: Record<
  ValuePropsVariation,
  string
> = {
  [ValuePropsVariation.WEIGHT]: "lastingResults-1",
  [ValuePropsVariation.WEIGHT_MED_COMPANION]: "lastingResults-2",
  [ValuePropsVariation.WEIGHT_DIABETES_SUPPORT]: "lastingResults-3",
  [ValuePropsVariation.WEIGHT_DIABETES_SUPPORT_MED_COMPANION]:
    "lastingResults-4",
  [ValuePropsVariation.DPP]: "lastingResults-5",
  [ValuePropsVariation.DPP_MED_COMPANION]: "lastingResults-2",
  [ValuePropsVariation.MED]: "lastingResults-6",
  [ValuePropsVariation.MED_DIABETES_SUPPORT]: "lastingResults-7",
  [ValuePropsVariation.NMPBA]: "lastingResults-7",
};

export const ProgramResultFooterSection = () => {
  const { enrollmentInfo, partnerInfo, selectedProgram, surveyAnswers } =
    useAppContext();
  const { valuePropsVariation } = useProgramResult();
  const { t } = useTranslation();

  const { noomMedMoreOptions, primaryCareProvider } = surveyAnswers;

  const ineligibleForDiabetesSupport =
    primaryCareProvider === PrimaryCareProviderSelection.NO;
  const ineligibleForMed =
    partnerOffersPrograms(["MED"], enrollmentInfo, partnerInfo) &&
    (selectedProgram === "WEIGHT" || selectedProgram === "DPP") &&
    noomMedMoreOptions !== NoomMedMoreOptionsSelection.OTHER;
  const isMedicalProgram = selectedProgram === null;

  const lastingResultsDisclaimerKeySuffix =
    lastingResultsDisclaimerKeySuffixMap[valuePropsVariation];
  const lastingResultsDisclaimerKey = `programResult.footerSection.disclaimers.${lastingResultsDisclaimerKeySuffix}`;

  return (
    <Box padding="var(--spacing-m)">
      <Stack gap="var(--spacing-m)">
        {ineligibleForMed && (
          <DisclaimerText>
            <Trans
              components={{
                supportEmail: NoomEmailAddressLink,
              }}
              i18nKey="programResult.footerSection.disclaimers.medIneligible"
            />
          </DisclaimerText>
        )}
        {ineligibleForDiabetesSupport && (
          <DisclaimerText>
            <Trans
              components={{
                supportEmail: NoomEmailAddressLink,
              }}
              i18nKey="programResult.footerSection.disclaimers.diabetesSupportIneligible"
            />
          </DisclaimerText>
        )}
        {isMedicalProgram && (
          <DisclaimerText>
            {t("programResult.footerSection.disclaimers.medCopayDisclaimer", {
              context: selectedProgram === "NMPBA" ? "accolade" : "",
            })}
          </DisclaimerText>
        )}
        <DisclaimerText>
          <Trans
            components={{
              dppStudyLink: (
                <Link
                  color={CompassColor.black}
                  href="https://go.noom.com/bmj-study"
                  target="_blank"
                  textDecoration="underline"
                />
              ),
            }}
            i18nKey={lastingResultsDisclaimerKey}
          />
        </DisclaimerText>
      </Stack>
    </Box>
  );
};
